import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import theme from './theme';
import RecipesPage from './views/recipes/RecipesPage';
import GroceryListPage from './views/grocery/GroceryListPage';
import MealIdeasPage from './views/ideas/MealIdeasPage';
import WhatCanIMakePage from './views/pantry/WhatCanIMakePage';
import LoginPage from './views/LoginPage';
import AppVersionBadge from './components/AppVersionBadge.jsx';

var AppContainer = styled.div`
  min-height: 100vh;
  background: ${theme.bgGradient};
`;

var NavBar = styled.nav`
  display: flex;
  align-items: center;
  gap: 0;
  padding: 0 16px;
  background: ${theme.bgDeep};
  border-bottom: 1px solid ${theme.border};
  overflow-x: auto;
  position: sticky;
  top: 0;
  z-index: 100;
`;

var NavTab = styled.button`
  padding: 14px 18px;
  border: none;
  border-bottom: 3px solid ${p => p.$active ? theme.accent : 'transparent'};
  background: transparent;
  color: ${p => p.$active ? '#fff' : theme.textMuted};
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: ${p => p.$active ? '700' : '400'};
  white-space: nowrap;
  transition: color 0.15s, border-color 0.15s;
  &:hover { color: #fff; }
`;

var NavSpacer = styled.div`
  flex: 1;
`;

var UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
`;

var UserName = styled.span`
  color: ${theme.textMuted};
  font-size: 0.8rem;
`;

var LogoutBtn = styled.button`
  padding: 6px 14px;
  border: 1px solid ${theme.border};
  border-radius: ${theme.radiusSm};
  background: transparent;
  color: #aaa;
  cursor: pointer;
  font-size: 0.8rem;
  &:hover { border-color: ${theme.error}; color: ${theme.error}; }
`;

var TABS = [
  { id: 'recipes', label: '🥗 Recipes' },
  { id: 'grocery', label: '🛒 Grocery' },
  { id: 'ideas', label: '🎲 Ideas' },
  { id: 'pantry', label: '🔍 What Can I Make?' },
];

var TAB_IDS = TABS.map(function (t) { return t.id; });

function readTabFromSearch() {
  try {
    var p = new URLSearchParams(window.location.search);
    var t = p.get('tab');
    if (t && TAB_IDS.indexOf(t) !== -1) return t;
  } catch (e) {}
  return 'recipes';
}

var App = function () {
  var [user, setUser] = useState(null);
  var [loading, setLoading] = useState(true);
  var [activeTab, setActiveTab] = useState(readTabFromSearch);

  var checkAuth = useCallback(async function () {
    try {
      var res = await fetch('/api/auth/me', { credentials: 'include' });
      if (res.ok) {
        var data = await res.json();
        setUser(data.user);
      }
    } catch (e) {}
    setLoading(false);
  }, []);

  useEffect(function () { checkAuth(); }, [checkAuth]);

  useEffect(function () {
    if (!user) return;
    var u = new URL(window.location.href);
    if (activeTab === 'recipes') {
      u.searchParams.delete('tab');
    } else {
      u.searchParams.set('tab', activeTab);
    }
    var q = u.searchParams.toString();
    var next = u.pathname + (q ? '?' + q : '');
    var cur = window.location.pathname + window.location.search;
    if (next !== cur) {
      window.history.replaceState({}, '', next);
    }
  }, [activeTab, user]);

  var handleLogin = async function (username, password) {
    var res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ username, password })
    });
    if (!res.ok) {
      var data = await res.json();
      throw new Error(data.error || 'Login failed');
    }
    var result = await res.json();
    setUser(result.user);
  };

  var handleRegister = async function (username, email, password) {
    var res = await fetch('/api/auth/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ username, email, password })
    });
    if (!res.ok) {
      var data = await res.json();
      throw new Error(data.error || 'Registration failed');
    }
    var result = await res.json();
    setUser(result.user);
  };

  var handleLogout = async function () {
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
    setUser(null);
  };

  if (loading) return <AppContainer />;

  if (!user) return <LoginPage onLogin={handleLogin} onRegister={handleRegister} />;

  var renderView = function () {
    switch (activeTab) {
      case 'grocery': return <GroceryListPage onLogout={handleLogout} />;
      case 'ideas': return <MealIdeasPage onLogout={handleLogout} />;
      case 'pantry': return <WhatCanIMakePage onLogout={handleLogout} />;
      default: return <RecipesPage user={user} onLogout={handleLogout} hideUserBar />;
    }
  };

  return (
    <AppContainer>
      <NavBar>
        {TABS.map(function (tab) {
          return (
            <NavTab key={tab.id} $active={activeTab === tab.id}
              onClick={function () { setActiveTab(tab.id); }}>
              {tab.label}
            </NavTab>
          );
        })}
        <NavSpacer />
        <AppVersionBadge style={{ color: theme.textMuted, flexShrink: 0 }} />
        <UserInfo>
          <UserName>{user.username}</UserName>
          <LogoutBtn onClick={handleLogout}>Sign Out</LogoutBtn>
        </UserInfo>
      </NavBar>
      {renderView()}
    </AppContainer>
  );
};

export default App;
